import React from "react";
import Card from "@/components/Card";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { ChartData } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { formatCurrency } from "@/utils/formatCurrency";
import { getDonutChartTooltipConfig } from "../helpers/utils";

interface DashboardDonutChartProps {
  title: string;
  data: any[];
  valueField: string;
  labelField: string;
  colorField: string;
  isCurrency?: boolean;
}

const DashboardDonutChart: React.FC<DashboardDonutChartProps> = ({
  title,
  data = [],
  valueField,
  labelField,
  colorField,
  isCurrency = false,
}) => {
  const theme = useTheme();

  try {
    if (!Array.isArray(data)) {
      data = [];
    }

    const total = data.reduce((sum, item) => {
      if (!item || typeof item !== "object") {
        return sum;
      }
      const value = item[valueField];
      const numericValue = typeof value === "number" ? value : 0;
      return sum + numericValue;
    }, 0);

    const chartData: ChartData<"doughnut", number[], string> = {
      labels: data.map((item) => {
        if (!item || typeof item !== "object") {
          return "Unknown";
        }
        const label = item[labelField];
        return label || "Unknown";
      }),
      datasets: [
        {
          data: data.map((item) => {
            if (!item || typeof item !== "object") {
              return 0;
            }
            const value = item[valueField];
            return typeof value === "number" ? value : 0;
          }),
          backgroundColor: data.map((item) => {
            if (!item || typeof item !== "object") {
              return "#E2E2E2";
            }
            const color = item[colorField];
            return color || "#E2E2E2";
          }),
          borderColor: data.map((item) => {
            if (!item || typeof item !== "object") {
              return "#E2E2E2";
            }
            return item[colorField] || "#E2E2E2";
          }),
          borderWidth: 1,
          spacing: 6,
          borderRadius: 4,
        },
      ],
    };

    return (
      <Card
        sx={{
          height: 450,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <Typography
          variant="cardHeader2"
          sx={{ mb: 2, fontSize: "14px", whiteSpace: "nowrap" }}
        >
          {title}
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          height="100%"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            width="100%"
          >
            {data.length === 0 ? (
              <Typography
                align="center"
                color="text.secondary"
                sx={{ fontSize: "13px" }}
              >
                No data
              </Typography>
            ) : (
              <>
                <Doughnut
                  style={{
                    width: "180px",
                    maxHeight: "180px",
                    minHeight: "180px",
                  }}
                  data={chartData}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: getDonutChartTooltipConfig(
                        data,
                        total,
                        valueField,
                        labelField,
                        isCurrency
                      ),
                    },
                    cutout: 50,
                  }}
                />
                <Box mt={3} width="100%">
                  {data.map((entry, i) => {
                    if (!entry || typeof entry !== "object") {
                      return null;
                    }

                    const value = entry[valueField];
                    const percent = total
                      ? Math.round((value / total) * 100)
                      : 0;

                    const numericValue = typeof value === "number" ? value : 0;
                    const displayValue = isCurrency
                      ? formatCurrency(numericValue)
                      : numericValue.toLocaleString();

                    return (
                      <Box
                        key={`${entry[labelField]}-${i}`}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              background: entry[colorField] || "#E2E2E2",
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "14px", fontWeight: 500 }}
                          >
                            {entry[labelField]} ({percent}%)
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
                          {displayValue}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={2}
          >
            <Typography
              variant="bodyRegular"
              color="text.secondary"
              sx={{ fontSize: "13px" }}
            >
              Total
            </Typography>
            <Typography sx={{ fontSize: "17px", fontWeight: 700 }}>
              {isCurrency
                ? formatCurrency(total || 0)
                : total?.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  } catch (error) {
    return (
      <Card sx={{ minHeight: 420, overflow: "hidden" }}>
        <Typography variant="cardHeader2" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="300px"
        >
          <Typography color="error">Error loading chart data</Typography>
        </Box>
      </Card>
    );
  }
};

export default DashboardDonutChart;
